"use client";

import { ChevronDown, Instagram, Youtube, Music2, Music, Mail, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { motion } from "framer-motion";
const shopLinks = [{
  label: "Aluminum Profiles",
  href: "/MaterialCatalogPage"
}, {
  label: "Architectural Hardware",
  href: "/MaterialCatalogPage"
}, {
  label: "Bulk Supply",
  href: "/MaterialCatalogPage"
}, {
  label: "Custom Extrusions",
  href: "/MaterialCatalogPage"
}];
const supportLinks = [{
  label: "Sales Inquiry",
  href: "/ConnectWithSalesPage"
}, {
  label: "Logistics Support",
  href: "/ConnectWithSalesPage"
}, {
  label: "Warehouse Locations",
  href: "/OurGlobalReachPage"
}, {
  label: "Contact Us",
  href: "/ConnectWithSalesPage"
}];
const socialLinks = [{
  label: "Instagram",
  icon: Instagram,
  href: "#"
}, {
  label: "YouTube",
  icon: Youtube,
  href: "#"
}, {
  label: "TikTok",
  icon: Music2,
  href: "#"
}, {
  label: "Spotify",
  icon: Music,
  href: "#"
}];
const legalLinks = [{
  label: "Privacy",
  href: "#"
}, {
  label: "Terms",
  href: "#"
}, {
  label: "Compliance",
  href: "#"
}];
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[0, 1, 2, 3].map(i => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-muted animate-pulse" />
          </li>)}
      </ul>;
  }
  const items = menuItems.length > 0 ? menuItems : [{
    id: "1",
    label: "About World Sky",
    href: "/OurGlobalReachPage"
  }, {
    id: "2",
    label: "Supply Chain",
    href: "/OurGlobalReachPage"
  }, {
    id: "3",
    label: "Careers",
    href: "#"
  }, {
    id: "4",
    label: "Press",
    href: "#"
  }];
  return <ul className="space-y-3">
      {items.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="group inline-flex items-center gap-2 text-sm font-default text-white hover:text-sky-500 transition-colors">
            <span className="h-1 w-1 bg-sky-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            <span className="group-hover:translate-x-0.5 transition-transform">
              {item.label}
            </span>
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const {
    ref: wordmarkRef,
    isVisible: wordmarkVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2787" id="corporate-footer" className="dark bg-slate-950 text-white pt-[120px] pb-[60px] px-6 md:px-10 lg:px-16 border-t border-slate-800 relative overflow-hidden">
      <div ref={wordmarkRef} className={`group relative mb-20 transition-all duration-1000 ${wordmarkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="font-default font-extrabold leading-[0.85] text-white select-none relative" style={{
        fontSize: "clamp(8rem, 18vw, 16rem)",
        letterSpacing: "-0.05em"
      }}>
          <span className="relative inline-block">
            WORLD
            <span className="text-sky-500 group-hover:animate-pulse inline-block">
              .
            </span>
            <span aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 h-px w-0 group-hover:w-full bg-sky-500/60 transition-[width] duration-1000 ease-linear" />
          </span>
        </h2>
        <p className="mt-6 font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-sky-500/80 max-w-2xl">
          SAB Professional - The Promise of Quality, Economy & Reliability
        </p>
      </div>

      <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 transition-all duration-1000 delay-150 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div>
          <h3 className="font-default text-[11px] uppercase tracking-[0.15em] text-white mb-6 font-semibold">
            Products
          </h3>
          <ul className="space-y-3">
            {shopLinks.map((link, i) => <li data-index={i} key={link.label}>
                <Link to={link.href} className="group inline-flex items-center gap-2 text-sm font-default text-white hover:text-sky-500 transition-colors">
                  <span className="h-1 w-1 bg-sky-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-0.5 transition-transform">
                    {link.label}
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>

        <div>
          <h3 className="font-default text-[11px] uppercase tracking-[0.15em] text-white mb-6 font-semibold">
            Support
          </h3>
          <ul className="space-y-3">
            {supportLinks.map((link, i) => <li data-index={i} key={link.label}>
                <Link to={link.href} className="group inline-flex items-center gap-2 text-sm font-default text-white hover:text-sky-500 transition-colors">
                  <span className="h-1 w-1 bg-sky-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-0.5 transition-transform">
                    {link.label}
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>

        <div>
          <h3 className="font-default text-[11px] uppercase tracking-[0.15em] text-white mb-6 font-semibold">
            Company
          </h3>
          <MenuProvider menu_id="17">
            <FooterMenuConsumer />
          </MenuProvider>
        </div>

        <div>
          <h3 className="font-default text-[11px] uppercase tracking-[0.15em] text-white mb-6 font-semibold">
            Connect
          </h3>
          <div className="flex items-center gap-3 mb-6">
            {socialLinks.map((social, i) => {
            const Icon = social.icon;
            return <Link data-index={i} key={social.label} to={social.href} aria-label={social.label} className="inline-flex items-center justify-center h-9 w-9 border border-slate-800 hover:border-sky-500 hover:text-sky-500 text-slate-400 transition-colors">
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                </Link>;
          })}
          </div>
          <address className="not-italic font-serif italic text-sm text-white leading-relaxed">
            <div>Sharjah Industrial Area</div>
            <div>Ajman & Al Ain, UAE</div>
            <div className="mt-4 space-y-2 font-default not-italic text-white">
              <Link to="tel:+971527750331" className="flex items-center gap-2 hover:text-sky-500 transition-colors">
                <Phone className="size-3 text-sky-500" />
                +971-527750-331
              </Link>
              <Link to="mailto:info@world-sky.ae" className="flex items-center gap-2 hover:text-sky-500 transition-colors">
                <Mail className="size-3 text-sky-500" />
                info@world-sky.ae
              </Link>
            </div>
          </address>
        </div>
      </div>

      <div className="mt-24 overflow-hidden border-y border-slate-800/50 py-4">
        <motion.div animate={{
        x: [0, -1000]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: "linear"
      }} className="flex whitespace-nowrap gap-12 items-center">
          {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="flex items-center gap-12 text-[10px] font-mono tracking-[0.5em] text-slate-500 uppercase">
              <span>Sharjah</span>
              <span className="text-sky-500">•</span>
              <span>Ajman</span>
              <span className="text-sky-500">•</span>
              <span>Al Ain</span>
              <span className="text-sky-500">•</span>
            </div>)}
        </motion.div>
      </div>

      <div className="mt-16 mb-8 relative">
        <div className="h-px w-full bg-slate-800" />
        <div className="absolute top-1/2 left-0 -translate-y-1/2 flex gap-16 w-full px-4 pointer-events-none">
          <div className="size-1 bg-sky-500 rotate-45" />
          <div className="size-1 bg-sky-500 rotate-45 ml-auto" />
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="font-default text-[11px] uppercase tracking-[0.15em] text-white">
          © 2023 SAB Professional. All Rights Reserved.
        </div>

        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.15em] font-default">
          {legalLinks.map((link, i) => <div data-index={i} key={link.label} className="flex items-center gap-3">
              <Link to={link.href} className="text-white hover:text-sky-500 transition-colors">
                {link.label}
              </Link>
              {i < legalLinks.length - 1 && <span className="h-1 w-1 bg-sky-500" aria-hidden="true" />}
            </div>)}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="border-slate-800 bg-transparent text-white hover:bg-slate-800 hover:text-white text-[11px] uppercase tracking-[0.15em] font-default h-9 px-3">
              EN / UAE
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800 text-white">
            <DropdownMenuItem className="text-[11px] uppercase tracking-[0.15em] font-default">
              EN / UAE
            </DropdownMenuItem>
            <DropdownMenuItem className="text-[11px] uppercase tracking-[0.15em] font-default">
              AR / UAE
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </section>;
}